import React, { useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import { CloseButton, Dialog, DialogActions, DialogContent, DialogTitle, Loading, SubmitButton } from 'components';
import { SuspendServiceForm } from 'forms';
import { useZone } from 'store/apis';

interface SuspendServiceDialogProps {
  visible?: boolean;
  setVisible?: (value: boolean) => void;
  submitButtonLabel?: string;
  zoneId: number;
}

export const SuspendServiceDialog = ({
  visible = false,
  setVisible = () => {},
  submitButtonLabel = 'Submit Entry',
  zoneId,
}: SuspendServiceDialogProps) => {
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
  const formRef = useRef<any>();
  const [loadingForm, setLoadingForm] = useState<boolean>(false);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const { data: { code = '', name = '' } = {}, isLoading: loading_zone } = useZone({ zoneId });

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onSubmit = () => {
    formRef.current.onSubmit();
  };

  return (
    <Dialog open={visible} onClose={onClose} PaperProps={{ sx: { overflow: 'visible' } }}>
      {loading_zone ? (
        <Loading text="One moment, loading zone..." height={400} />
      ) : (
        <>
          <DialogTitle sx={{ paddingX: down_sm ? 3 : 8 }}>
            <Box component="div">
              <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                Would you like to suspend the scheduled service for{' '}
                <Typography component="span" sx={{ color: theme.palette.primary.main, fontSize: 18, fontWeight: 600 }}>
                  premier zone {`#${code}-${name}`}?
                </Typography>
              </Typography>

              <Typography color="textSecondary" sx={{ fontSize: 12, marginTop: 0.5 }}>
                Suspending the zone will temporarily turn off the premier zone schedule for the remainder of the day.
                Premier zone schedule will resume the following business day. Team members are encouraged to service
                zones regardless of suspension of premier zone(s). Comments collected will be included in the{' '}
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{ color: theme.palette.primary.main, fontSize: 12, fontStyle: 'italic', fontWeight: 600 }}
                >
                  service zone report
                </Typography>
                . To disable a premier zone indefinitely please visit the zone section.
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent>
            <SuspendServiceForm
              ref={formRef}
              zoneId={zoneId}
              onInit={(loading) => setLoadingForm(loading)}
              onSubmitting={(loading) => setLoadingSubmit(loading)}
              next={() => {
                onClose(null, 'anyway');
              }}
            />
          </DialogContent>

          <DialogActions>
            <SubmitButton
              loading={loadingSubmit}
              disabled={loadingForm || loadingSubmit}
              label={submitButtonLabel}
              onClick={onSubmit}
            />
          </DialogActions>
        </>
      )}

      <CloseButton onClick={() => onClose(null, 'anyway')} />
    </Dialog>
  );
};
